import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

import mainCourseImg from '../assets/images/main_course.webp';
import practiceCourseImg from '../assets/images/practice_course.webp';
import challengeEventImg from '../assets/images/challenge_event.webp';
import campLifeImg from '../assets/images/camp_life.webp';

const features = [
  {
    title: '主線課程',
    desc: '本營隊的課程設計採二軌並行：基礎與進階。從初學者到進階挑戰者，每位學員都能依程度找到合適的起點。\n基礎班將從程式語法與邏輯思維入門，建立穩固基礎與實作練習；進階班則挑戰資料結構與演算法設計，深化問題解決與效能優化能力。\n透過分軌教學與精心規劃的學習路徑，讓每位學員都能在自己的節奏中，獲得最適化的學習體驗。',
    clipPath: 'polygon(0% 15%, 20% 5%, 40% 15%, 60% 5%, 80% 15%, 100% 5%, 100% 85%, 80% 95%, 60% 85%, 40% 95%, 20% 85%, 0% 95%)',
    bg: 'bg-gradient-to-br from-[rgba(147,197,253,0.15)] to-[rgba(203,213,225,0.15)]',
    image: mainCourseImg,
    alignClass: 'items-start pt-12',
  },
  {
    title: '實作課程',
    desc: '營隊核心課程以「邏輯 × 創造 × 解決」為主軸，結合運算思維與實際程式開發。\n課程內容涵蓋變數、迴圈、遞迴、資料結構、圖論等核心主題，帶領學員經歷從「問題建構 → 解法設計 → 程式實作 → 結果驗證」的完整思考流程。\n在講師引導與實作練習並進的學習環境中，學員不僅能掌握語法，更能學會如何用程式思維分析與解決真實世界的問題。',
    clipPath: 'polygon(0% 15%, 20% 5%, 40% 15%, 60% 5%, 80% 15%, 100% 5%, 100% 85%, 80% 95%, 60% 85%, 40% 95%, 20% 85%, 0% 95%)',
    bg: 'bg-gradient-to-br from-[rgba(147,197,253,0.15)] to-[rgba(203,213,225,0.15)]',
    image: practiceCourseImg,
    alignClass: 'items-center',
  },
  {
    title: '挑戰活動',
    desc: '為驗證學習成果並強化整合應用力，營期最後一天將舉辦「程式實作挑戰賽」。\n學員需在限時內完成跨主題題目，將一週的學習成果轉化為具體表現。\n挑戰賽結束後，講師將針對作品進行講評與策略解析，協助學員了解自己的盲點與優勢，從錯誤中成長，提升面對問題的結構化思考與臨場應變能力。',
    clipPath: 'polygon(0% 15%, 20% 5%, 40% 15%, 60% 5%, 80% 15%, 100% 5%, 100% 85%, 80% 95%, 60% 85%, 40% 95%, 20% 85%, 0% 95%)',
    bg: 'bg-gradient-to-br from-[rgba(147,197,253,0.15)] to-[rgba(203,213,225,0.15)]',
    image: challengeEventImg,
    alignClass: 'items-center',
  },
  {
    title: '營隊生活',
    desc: '除了課程學習之外，營隊同樣注重團隊合作與人際交流。\n實體營地位於陽明交通大學校園內，課後安排多元夜間活動，如「新竹美食探索」、「程式趣味競賽」等。\n透過非正式的互動場合，學員能交流想法、分享學習經驗、認識志同道合的夥伴，並在輕鬆的氛圍中建立人脈、激發創意，打造兼具學習與生活的完整營隊體驗。',
    clipPath: 'polygon(0% 15%, 20% 5%, 40% 15%, 60% 5%, 80% 15%, 100% 5%, 100% 85%, 80% 95%, 60% 85%, 40% 95%, 20% 85%, 0% 95%)',
    bg: 'bg-gradient-to-br from-[rgba(147,197,253,0.15)] to-[rgba(203,213,225,0.15)]',
    image: campLifeImg,
    alignClass: 'items-center',
  },
];

const MobileFeatures = () => {
  return (
    <section className="py-8 px-4 bg-gradient-to-br from-[#e6f0fa]/90 to-[#ffffff]/90 backdrop-blur-sm">
      <motion.h2
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-3xl font-extrabold text-center bg-gradient-to-r from-[#60a5fa] to-[#93c5fd] bg-clip-text text-transparent mb-8"
      >
        活動內容四大特色
      </motion.h2>
      <div className="flex flex-col gap-6">
        {features.map((f, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: idx * 0.1 }}
            className={`${f.bg} border-2 border-[#e0e7ff]/50 p-6 rounded-lg shadow-lg`}
          >
            <img
              src={f.image}
              alt={`${f.title} illustration`}
              className="w-full h-[140px] object-cover rounded-lg border-2 border-[#e0e7ff]/50 mb-4"
            />
            <h3 className="text-lg font-extrabold text-center bg-gradient-to-r from-[#60a5fa] to-[#93c5fd] bg-clip-text text-transparent">
              {f.title}
            </h3>
            <p className="mt-2 text-sm text-[#475569] text-center whitespace-pre-wrap">
              {f.desc}
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const DesktopFeatures = () => {
  return (
    <section className="py-12 px-8 bg-gradient-to-br from-[#e6f0fa]/90 to-[#ffffff]/90 backdrop-blur-sm">
      <motion.h2
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-5xl font-extrabold text-center bg-gradient-to-r from-[#60a5fa] to-[#93c5fd] bg-clip-text text-transparent mb-10"
      >
        活動內容四大特色
      </motion.h2>
      <div className="max-w-7xl mx-auto flex flex-col">
        {features.map((f, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: idx * 0.1 + 0.2 }}
            className={`min-h-[320px] max-w-7xl mx-auto ${f.bg} border-2 border-[#e0e7ff]/50 ${idx === 0 ? 'mt-0' : 'mt-[-8rem]'} p-8 z-[${10 - idx}] rounded-lg shadow-lg`}
            style={{ clipPath: f.clipPath }}
          >
            <div className={`max-w-6xl mx-auto bg-transparent flex ${f.alignClass} justify-center min-h-[320px] gap-8`}>
              <div className={`flex-[0 0 260px] ${idx % 2 === 0 ? 'order-1' : 'order-2'} mx-auto`}>
                <img
                  src={f.image}
                  alt={`${f.title} illustration`}
                  className="w-[240px] h-[160px] object-cover rounded-lg border-2 border-[#e0e7ff]/50 mx-auto shadow-md"
                />
              </div>
              <div className={`flex-1 ${idx % 2 === 0 ? 'order-2' : 'order-1'} mx-auto`}>
                <h3 className="text-2xl font-extrabold bg-gradient-to-r from-[#60a5fa] to-[#93c5fd] bg-clip-text text-transparent text-center">
                  {f.title}
                </h3>
                <p className="mt-3 whitespace-pre-wrap text-base text-[#475569] text-center">
                  {f.desc}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const Features = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile ? <MobileFeatures /> : <DesktopFeatures />;
};

export default Features;